import { FORM_IS_DIRTY_ARE_YOU_SURE_YOU_WANT_TO_LEAVE } from 'mk/autogenerated/translations/form.da3a79c0cd2d1c25446db9af6f593ea7'
import PhotosUploadScreen from 'mk2/apps/forum/containers/PhotosUploadScreen/PhotosUploadScreen';
import Interpolate, { OwnProps as InterpolateProps } from 'mk2/components/Interpolate';
import { FormPageType } from 'mk2/constants';
import { PhotosUploadConfig } from 'mk2/containers/PhotosUpload/PhotosUpload';
import { XHRFormError, XHRPostError } from 'mk2/helpers/api';
import { cacheLast } from 'mk2/helpers/cache';
import { mkReduxFormCancelPage, mkReduxFormClosePage, mkReduxFormOpenPage } from 'mk2/helpers/form.actions';
import Loadable from 'mk2/helpers/Loadable';
import { showErrorToast, showServerErrorToast, showSuccessToast } from 'mk2/helpers/toasts';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { Logger } from 'mk2/logger';
import { AppState, getFormState, getRequestDeviceMobile } from 'mk2/reducers';
import { redirectInPWASaga } from 'mk2/services/browserHistory';
import React from 'react';
import { connect, MapDispatchToPropsFunction } from 'react-redux';
import { Prompt } from 'react-router';
import { bindActionCreators } from 'redux';
import {
    FormErrors,
    initialize,
    InjectedFormProps as ReduxFormInjectedProps,
    reduxForm,
    reset,
    stopSubmit,
    SubmissionError,
} from 'redux-form';
import { put } from 'redux-saga/effects';

const StickerField = Loadable({
    loader: () => import('mk2/components/forms/StickerField' /* webpackChunkName: "components.forms.StickerField" */),
    modules: ['mk2/components/forms/StickerField'],
    webpack: () => [ require.resolveWeak('mk2/components/forms/StickerField') ],
});

export type CursorPosition = 'start' | 'end' | 'select';
export type AutoFocus = boolean | CursorPosition;

export interface DjangoFormFieldError {
    code: string;
    message: string;
    messageParams?: {[key: string]: string};
}

export interface DjangoFormErrors {
    [fieldName: string]: DjangoFormFieldError[];
}

export function* handleXHRFormSuccessSaga(
    reduxFormName: string,
    gotoUrl?: string,
    toastSuccessMessage?: React.ReactNode,
    newInitialValues?: any,
) {
    // Stop submit
    yield put(stopSubmit(reduxFormName));

    // Reset form
    yield put(reset(reduxFormName));

    if (newInitialValues) {
        yield put(initialize(reduxFormName, newInitialValues));
    }

    // Display toast message
    if (toastSuccessMessage) {
        yield showSuccessToast(toastSuccessMessage);
    }

    if (gotoUrl) {
        yield redirectInPWASaga(gotoUrl);
    }
}

export function* handleXHRFormErrorSaga(reduxFormName: string, error: XHRFormError | XHRPostError, logger: Logger) {
    // Bad request - form invalid
    if (error.status === 400 && 'errors' in error.response.body) {
        const djFormErrors: DjangoFormErrors = error.response.body.errors;

        const reduxFormErros = {};
        Object.keys(djFormErrors).forEach((fieldName) => {
            const fieldErrors: DjangoFormFieldError[] = djFormErrors[fieldName];

            const formattedMessages: React.ReactChild[] = [];
            fieldErrors.forEach((fieldError) => {
                if (fieldError.messageParams) {
                    const formattedParams: { [name: string]: React.ReactNode } = {};
                    Object.keys(fieldError.messageParams).forEach((name) => {
                        let value: React.ReactChild = fieldError.messageParams[name];
                        if (name === 'url') {
                            value = <a href={value}>{value}</a>;
                        }
                        formattedParams[name] = value;
                    });

                    const props: InterpolateProps = {
                        key: formattedMessages.length,
                        className: 'errormsg',
                        i18nKey: fieldError.message,
                        useDangerouslySetInnerHTML: true,
                        ...formattedParams,
                    };

                    formattedMessages.push(
                        // <Interpolate> generates <span> element with text inside
                        <Interpolate {...props} />,
                    );
                } else {
                    formattedMessages.push(
                        <span
                            key={formattedMessages.length}
                            className="errormsg"
                            dangerouslySetInnerHTML={{ __html: fieldError.message }}
                        />,
                    );
                }
            });

            // error message for one field if span of span:
            // <span class="errors">
            //     <span> error msg1 </span>
            //     <span> error msg2 </span>
            // </span>
            reduxFormErros[fieldName] = <span className="errormsg_list">{...formattedMessages}</span>;
        });

        yield put(stopSubmit(reduxFormName, reduxFormErros));
    } else if (error.status === 403 && 'error' in error.response.body) {
        yield put(stopSubmit(reduxFormName));
        yield showErrorToast(error.response.body.error);
    } else {
        logger.error(error);
        yield put(stopSubmit(reduxFormName));
        yield showServerErrorToast();
    }
}

interface MKReduxFormConfig<FormData, Props> {
    formName?: string;
    photosConfig?: PhotosUploadConfig;
    onValidate?: (values: FormData, props: Props) => FormErrors<FormData>;
    onSave: (values: FormData, props: Props) => void;
    onChange?: (newValues: FormData, oldValues: FormData, props: Props) => void;
    disableDirtyCheck?: boolean;
}

// every component where @mkReduxFormNew() decorator is applied must contain these attributes
interface MKReduxFormOwnProps {
}

// state props, ktore posielame dole do obaleneho komponenty
interface MKReduxFormStateProps {
    currentFormPage: FormPageType;
    mobile: boolean;
}

// dispatch props, ktore posielame dole do obaleneho komponenty
interface MKReduxFormDispatchProps {
    onOpenFormPage(formPage: FormPageType);
    onCloseFormPage();
    onCancelFormPage();
}

// interface, ktory sa vklada do React.Component<?? & MKReduxFormInjectedProps> form componentu
export type MKReduxFormInjectedProps<FormData = {}, P = {}, ErrorType = string> = MKReduxFormStateProps & MKReduxFormDispatchProps & ReduxFormInjectedProps<FormData, P, ErrorType>;

// aky typ komponentov berie a vracia @mkReduxFormNew dekorator
type MKReduxFormDecorator<Props, FormData = {}, ErrorType = string> =
    (WrappedComponent: React.ComponentType<Props & MKReduxFormInjectedProps<FormData, Props, ErrorType>>) =>
        React.ComponentClass<Props>;

export function mkReduxFormNew<FormData, Props extends MKReduxFormOwnProps>(config: MKReduxFormConfig<FormData, Props>): MKReduxFormDecorator<Props, FormData> {
    const f = reduxForm<FormData, Props>({
        form: config.formName,
        persistentSubmitErrors: true,
        onSubmit: (values: FormData, dispatch, props: Props) => {
            if (config.onValidate) {
                const errors = config.onValidate(values, props);
                if (Object.keys(errors).length) {
                    return Promise.reject(new SubmissionError(errors));
                }
            }

            // redux-form set isSubmitting based on value returned
            // because we are submiting async, we need to return Promise
            // and because we are submiting using sagas, we fake this promise as never resolved
            return Promise.all([
                Promise.resolve().then(() => {
                    // musi byt v promise, aby sa akcia START_SUBMIT firovala este pred zavolanim onSave()
                    //
                    // Pretoze ked onSave() zavola stopSubmit(), fireje tym akciu STOP_SUBMIT a potom sa
                    // tieto dve akcie udeju v opacnom poradi (najskor STOP_SUBMIT a potom START_SUBMIT)
                    //
                    // To sposobi, ze form ostane v zlom stave (isSubmitting = true)
                    config.onSave(values, props);
                }),
                new Promise(() => {
                    // Never ending (resolved) story :D
                    return;
                }),
            ]);
        },
        onChange: !config.onChange
            ? undefined
            : (newValues: Partial<FormData>, dispatch, props: Props, oldValues: Partial<FormData>) => {
                // vyzera ze redux-form posiela cele FormData a nie iba Partial<FormData>
                config.onChange(newValues as FormData, oldValues as FormData, props);
            },
    });

    const g = (WrappedComponent: React.ComponentType<Props & MKReduxFormInjectedProps<FormData, Props>>) => {
        class MKFormHoC extends React.Component<Props & MKReduxFormInjectedProps<FormData, Props>> {
            public render() {
                const { dirty, currentFormPage, form } = this.props;

                return (
                    // pouzity fragment a nie div, aby sa flex-layout v parent containeri vztahoval na form fieldy
                    <React.Fragment>
                        {!config.disableDirtyCheck && (
                            <Prompt when={dirty} message={FORM_IS_DIRTY_ARE_YOU_SURE_YOU_WANT_TO_LEAVE} />
                        )}
                        {currentFormPage === FormPageType.Stickers ? (
                                <StickerField
                                    name="sticker"
                                />
                            ) : currentFormPage === FormPageType.Photos ? (
                                    <PhotosUploadScreen
                                        formName={form}
                                        config={config.photosConfig}
                                    />
                                ) : (
                                    <WrappedComponent {...this.props} />
                                )
                        }
                    </React.Fragment>
                );
            }
        }
        return connect<MKReduxFormStateProps, MKReduxFormDispatchProps, Props & ReduxFormInjectedProps<FormData, Props>>(mkReduxFormMapStateToPropsInternal, mkReduxFormMapDispatchToPropsInternal)(MKFormHoC);
    };

    return (c: React.ComponentType<Props & MKReduxFormInjectedProps<FormData, Props>>) => f(g(c));
}

const mkReduxFormMapStateToPropsInternal = (state: AppState, { form }: Pick<ReduxFormInjectedProps, 'form'>): MKReduxFormStateProps => {
    const formState = getFormState(state, form);
    return {
        currentFormPage: formState.currentFormPage,
        mobile: getRequestDeviceMobile(state),
    };
};

export const mkReduxFormMapStateToProps = (formName: string) => (state: AppState) => mkReduxFormMapStateToPropsInternal(state, { form: formName });

export const mkReduxFormMapDispatchToProps = (formName: string): MapDispatchToPropsObject<MKReduxFormDispatchProps> => ({
    onOpenFormPage: (formPage) => mkReduxFormOpenPage(formName, formPage),
    onCloseFormPage: () => mkReduxFormClosePage(formName),
    onCancelFormPage: () => mkReduxFormCancelPage(formName),
});

const mkReduxFormMapDispatchToPropsInternal: MapDispatchToPropsFunction<MKReduxFormDispatchProps, Pick<ReduxFormInjectedProps, 'form'>>
    = (dispatch, { form }): MKReduxFormDispatchProps => bindActionCreators(mkReduxFormMapDispatchToProps(form), dispatch);

export function autoFocusElement(element: HTMLInputElement | HTMLTextAreaElement, autoFocus: boolean | CursorPosition, value: string) {
    if (autoFocus) {
        element.focus();

        if ((autoFocus === 'select' || autoFocus === true)
            // The input element's type ('number') does not support selection.
            && element.type !== 'number') {
            element.setSelectionRange(0, value.length);
            element.scrollTop = 0;
        } else if (autoFocus === 'start') {
            element.setSelectionRange(0, 0);
            element.scrollTop = 0;
        } else if (autoFocus === 'end') {
            element.setSelectionRange(value.length, value.length);
            element.scrollTop = 0;
        }
    }
}

export const normalizeToInt = (value) => value && parseInt(value, 10);

export const normalizeToFloat = (value) => value && parseFloat(value);

export const normalizeToDecimal = (value) => value && Math.round(parseFloat(value) * 100) / 100;

const initialValuesCache = cacheLast();

/*
 * This function helps to avoid unnecessary re-renders of react components because of changing
 * initialValues object.
 *
 * In naive case, mapStateToProps() always creates new object for initialValues, e.g.
 *
 * return {
 *     .... some other props
 *     initialValues: {
 *        field1: 'value1'
 *        field2: 'value2'
 * }
 *
 * so we cache the last initialValues object to avoid re-renders.
 */
export function cachedInitialValues<FormData>(formName, initialValues: FormData, ...cacheKeys): FormData {
    return initialValuesCache(initialValues, formName, ...cacheKeys);
}
